import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiSearch, FiBarChart2 } = FiIcons;

// Common English stop words to exclude
const stopWords = [
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'for', 'if', 'in', 'into', 
  'is', 'it', 'no', 'not', 'of', 'on', 'or', 'such', 'that', 'the', 'their', 'then', 
  'there', 'these', 'they', 'this', 'to', 'was', 'will', 'with', 'what', 'which', 'who',
  'how', 'when', 'where', 'why', 'your', 'from', 'have', 'has', 'had', 'do', 'does',
  'did', 'can', 'could', 'would', 'should', 'may', 'might', 'must', 'shall'
];

const KeywordAnalysis = ({ questions }) => {
  const [topKeywords, setTopKeywords] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const [uniqueWordCount, setUniqueWordCount] = useState(0);
  const [lexicalDiversity, setLexicalDiversity] = useState(0);

  useEffect(() => {
    if (!questions.length) return;
    
    // Combine all question texts
    const combinedText = questions.map(q => q.text.toLowerCase()).join(' ');
    
    // Tokenize and filter stop words
    const words = combinedText
      .split(/[^a-z0-9']+/i)
      .filter(word => word.length > 1) // Filter out single letters
      .filter(word => !stopWords.includes(word.toLowerCase())); // Filter out stop words
    
    // Count word frequencies
    const wordFrequency = {};
    words.forEach(word => {
      wordFrequency[word] = (wordFrequency[word] || 0) + 1;
    });
    
    // Sort by frequency
    const sortedWords = Object.entries(wordFrequency)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 30);
    
    setTopKeywords(sortedWords);
    setWordCount(words.length);
    setUniqueWordCount(Object.keys(wordFrequency).length);
    setLexicalDiversity(parseFloat((Object.keys(wordFrequency).length / words.length).toFixed(3)));
    
  }, [questions]);
  
  // Function to get color based on frequency rank
  const getBarColor = (index) => {
    const colors = [
      'bg-blue-500', 'bg-blue-400', 'bg-blue-300',
      'bg-indigo-500', 'bg-indigo-400', 'bg-indigo-300',
      'bg-purple-500', 'bg-purple-400', 'bg-purple-300',
      'bg-pink-500', 'bg-pink-400', 'bg-pink-300'
    ];
    return colors[index % colors.length];
  };
  
  // Get max frequency for scaling
  const maxFrequency = topKeywords.length > 0 ? topKeywords[0][1] : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiSearch} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Keyword & Term Frequency Analysis</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-1">Total Words</h4>
          <p className="text-3xl font-bold text-blue-700">{wordCount}</p>
          <p className="text-sm text-gray-600">After filtering stop words</p>
        </div>
        
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-1">Unique Words</h4>
          <p className="text-3xl font-bold text-indigo-700">{uniqueWordCount}</p>
          <p className="text-sm text-gray-600">Distinct terms used</p>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <h4 className="font-medium text-gray-800 mb-1">Lexical Diversity</h4>
          <p className="text-3xl font-bold text-purple-700">{lexicalDiversity}</p>
          <p className="text-sm text-gray-600">Type-Token Ratio (TTR)</p>
        </div>
      </div>
      
      <h4 className="font-medium text-gray-800 mb-4">Top Keywords</h4>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {topKeywords.slice(0, 15).map(([word, frequency], index) => (
          <div key={word} className="flex items-center space-x-2">
            <div className="w-full bg-gray-100 rounded-lg h-8 relative">
              <div 
                className={`absolute left-0 top-0 h-8 rounded-lg ${getBarColor(index)}`}
                style={{ width: `${(frequency / maxFrequency) * 100}%` }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-between px-3">
                <span className="font-medium text-sm text-gray-800">{word}</span>
                <span className="text-sm text-white font-bold">{frequency}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-8 bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">Lexical Diversity Interpretation</h4>
        <p className="text-sm text-gray-600">
          {lexicalDiversity < 0.3 ? (
            "Low lexical diversity suggests repetitive vocabulary. Consider introducing more varied terms."
          ) : lexicalDiversity < 0.5 ? (
            "Moderate lexical diversity, typical for focused academic content."
          ) : (
            "High lexical diversity indicates varied vocabulary usage across questions."
          )}
        </p>
      </div>
    </div>
  );
};

export default KeywordAnalysis;